import React, { useState, useEffect } from "react";
import { useTheme } from "@mui/material";
import {
  systemTypeService,
  tradeService,
  issueDescriptionService,
} from "@miview/api";
import {
  MiPageHeader,
  MiButton,
  MiList,
  MiModal,
  MiDetailFields,
  MiIcon,
  MiModalConfirm,
} from "@miview/components";
import { useEdit, useComponentState } from "@miview/hooks";
import { toList } from "@miview/utils";
import { PAGINATION, HTTP_STATUSES } from "@miview/constants";
import { mdiDotsGrid, mdiPencil, mdiPlus, mdiDelete } from "@mdi/js";

export const ItemFailuresTab = (props) => {
  const { tabs } = props;
  const [isNewReasonModalOpen, setIsNewReasonModalOpen] = useState(false);
  const [isEditReasonModalOpen, setIsEditReasonModalOpen] = useState(false);
  const [isDeleteReasonModalOpen, setIsDeleteReasonModalOpen] = useState(false);
  const [editSelection, setEditSelection] = useState({
    description: "",
    causeId: "",
    defaultFaultTradeId: "",
    descriptionSpanish: "",
  });
  const [causeIdObjs, setCauseIdObjs] = useState([]);
  const [trades, setTrades] = useState([]);
  const [refresh, setRefresh] = useState(false);
  const [itemFailures, setItemFailures] = useState([]);
  const stateManager = useComponentState();
  const theme = useTheme();
  const edit = useEdit(editSelection);

  useEffect(() => {
    getCauseIds();
    getTrades();
  }, [refresh]);

  const saveNewItemFailureType = async () => {
    return stateManager.run(async () => {
      const response = await issueDescriptionService.create(edit.edits);
      if (response.status === HTTP_STATUSES.OK) {
        closeNewModal();
        setRefresh(!refresh);
      }
    });
  };

  const editFailureType = async () => {
    return stateManager.run(async () => {
      const response = await issueDescriptionService.update(
        edit.getValue("id"),
        edit.edits
      );
      if (response.status === HTTP_STATUSES.OK) {
        closeEditModal();
        setRefresh(!refresh);
      }
    });
  };

  const deleteNewFailureType = async () => {
    return stateManager.run(async () => {
      const response = await issueDescriptionService.delete(
        edit.getValue("id")
      );

      if (response.status === HTTP_STATUSES.OK) {
        closeDeleteModal();
        edit.reset();
        setRefresh(!refresh);
      }
    });
  };

  const getCauseIds = async () => {
    return stateManager.run(async () => {
      const response = await systemTypeService.getSystemTypesByName({
        name: "IssueType",
      });

      if (response.status === HTTP_STATUSES.OK) {
        setCauseIdObjs(toList(response.data, "systemTypeId", "mainValue"));
      }
    });
  };

  const getTrades = async () => {
    return stateManager.run(async () => {
      const response = await tradeService.getAll();

      if (response.status === HTTP_STATUSES.OK) {
        setTrades(toList(response.data, "id", "name"));
      }
    });
  };

  const openNewModal = () => {
    setIsNewReasonModalOpen(!isNewReasonModalOpen);
    edit.reset();
  };

  const closeNewModal = () => {
    setIsNewReasonModalOpen(!isNewReasonModalOpen);
    edit.reset();
  };

  const openEditModal = (row) => {
    setEditSelection(row);
    setIsEditReasonModalOpen(!isEditReasonModalOpen);
  };

  const closeEditModal = () => {
    edit.reset();
    setEditSelection({});
    setIsEditReasonModalOpen(!isEditReasonModalOpen);
  };

  const openDeleteModal = (row) => {
    setEditSelection(row);
    setIsDeleteReasonModalOpen(!isDeleteReasonModalOpen);
  };

  const closeDeleteModal = () => {
    edit.reset();
    setEditSelection({});
    setIsDeleteReasonModalOpen(!isDeleteReasonModalOpen);
  };

  const getRowActions = () => {
    return [
      {
        text: "EDIT",
        onClick: openEditModal,
        renderIcon: () => <MiIcon path={mdiPencil} size={1} />,
      },
      {
        text: "DELETE",
        onClick: openDeleteModal,
        renderIcon: () => <MiIcon path={mdiDelete} size={1} />,
      },
    ];
  };

  const columns = [
    {
      field: "description",
      headerName: "Name",
      headerAlign: "left",
      align: "left",
      flex: 1,
    },
    {
      field: "causeId",
      headerName: "Cause ID",
      headerAlign: "left",
      align: "left",
      flex: 1,
      valueGetter: ({ value }) =>
        causeIdObjs.find((elem) => elem.value === value)?.text,
    },
    {
      field: "defaultFaultTradeId",
      headerName: "Default Trade",
      headerAlign: "left",
      align: "left",
      flex: 1,
      valueGetter: ({ value }) =>
        trades.find((elem) => elem.value === value)?.text,
    },
    {
      field: "descriptionSpanish",
      headerName: "Spanish Name",
      headerAlign: "left",
      align: "left",
      flex: 1,
    },
  ];

  const manageAreaFields = [
    {
      label: "Name",
      value: edit.getValue("description"),
      required: true,
      setValue: (e) => edit.update({ description: e }),
      width: "50%",
    },
    {
      label: "Cause ID",
      fieldType: "select",
      options: causeIdObjs,
      value: edit.getValue("causeId"),
      setValue: (e) => {
        edit.update({ causeId: e });
      },
      required: true,
      width: "50%",
    },
    {
      label: "Default Trade",
      fieldType: "select",
      options: trades,
      value: edit.getValue("defaultFaultTradeId"),
      setValue: (e) => {
        edit.update({ defaultFaultTradeId: e });
      },
      required: true,
      width: "50%",
    },
    {
      label: "Spanish Name",
      value: edit.getValue("descriptionSpanish"),
      setValue: (e) => edit.update({ descriptionSpanish: e }),
      width: "50%",
      required: true,
    },
  ];

  const actionButton = () => {
    return (
      <MiButton
        title="Item Failure Reason"
        icon={mdiPlus}
        inverse={true}
        onClick={openNewModal}
        color={theme.palette.primary.main}
      />
    );
  };

  const renderHeaderIcon = () => {
    return (
      <MiIcon path={mdiDotsGrid} size={1} color={theme.palette.primary.main} />
    );
  };
  const REQUIRED_FIELDS = [
    "description",
    "causeId",
    "defaultFaultTradeId",
    "descriptionSpanish",
  ];
  return (
    <div>
      <MiPageHeader
        title="Failure Reasons"
        leftIcon={renderHeaderIcon()}
        color={theme.palette.primary.main}
        action={actionButton()}
        loading={stateManager.isBusy()}
      />
      <MiList
        data={itemFailures}
        tabs={tabs}
        setData={setItemFailures}
        dataProvider={issueDescriptionService.getAll}
        columns={columns}
        disableColumnMenu
        paginationMode={PAGINATION.CLIENT}
        getRowActions={getRowActions}
        refresh={refresh}
      />
      <MiModal
        key={0}
        maxWidth={800}
        title="Create Item Failure Reason"
        open={isNewReasonModalOpen}
        onClose={closeNewModal}
        actions={[
          {
            name: "Cancel",
            style: { minWidth: 90 },
            onClick: closeNewModal,
            color: theme.palette.primary.grey,
            inverse: false,
          },
          {
            name: "Save",
            onClick: saveNewItemFailureType,
            style: { minWidth: 90 },
            color: theme.palette.primary.green,
            inverse: true,
            disabled: !edit.allFilled(...REQUIRED_FIELDS),
          },
        ]}
      >
        <MiDetailFields detailFields={manageAreaFields} />
      </MiModal>
      <MiModal
        key={1}
        maxWidth={800}
        title="Edit Failure Reason"
        open={isEditReasonModalOpen}
        onClose={closeEditModal}
        actions={[
          {
            name: "Cancel",
            style: { minWidth: 90 },
            onClick: closeEditModal,
            color: theme.palette.medium.grey,
            inverse: false,
          },
          {
            name: "Save",
            onClick: editFailureType,
            style: { minWidth: 90 },
            color: theme.palette.primary.green,
            inverse: true,
            disabled: !edit.allFilled(...REQUIRED_FIELDS),
          },
        ]}
      >
        <MiDetailFields detailFields={manageAreaFields} />
      </MiModal>
      <MiModalConfirm
        title={"Confirm Delete"}
        description={`Delete Failure Reason: ${editSelection.description}?`}
        open={isDeleteReasonModalOpen}
        handlePositive={deleteNewFailureType}
        handleNegative={closeDeleteModal}
      />
    </div>
  );
};
